import { PaperclipIcon } from "lucide-react";
import type { ComponentProps } from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Empty, EmptyContent, EmptyDescription } from "@/components/ui/empty";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";
import { formatDistanceToNow } from "./date-format";
import type { MailListItem } from "./types";
import { useMail } from "./use-mail";

interface MailListProps {
	items: MailListItem[];
	isLoading: boolean;
	onLoadMore?: () => void;
	canLoadMore?: boolean;
}

export function MailList({
	items,
	isLoading,
	onLoadMore,
	canLoadMore,
}: MailListProps) {
	const [mail, setMail] = useMail();

	if (isLoading && items.length === 0) {
		return (
			<Empty className="p-6">
				<EmptyContent>
					<EmptyDescription>Loading…</EmptyDescription>
				</EmptyContent>
			</Empty>
		);
	}

	if (items.length === 0) {
		return (
			<Empty className="p-6">
				<EmptyContent>
					<EmptyDescription>No messages yet.</EmptyDescription>
				</EmptyContent>
			</Empty>
		);
	}

	return (
		<ScrollArea className="h-full min-h-0 flex-1">
			<div className="flex flex-col divide-y">
				{items.map((item) => {
					const name = item.fromName ?? item.fromEmail;
					const isSelected = mail.selected === item._id;
					return (
						<Button
							key={item._id}
							variant="ghost"
							onClick={() =>
								setMail({
									...mail,
									selected: item._id,
								})
							}
							className={cn(
								"flex h-auto flex-col items-start gap-1 rounded-none px-4 py-2 text-left text-sm whitespace-normal transition-all hover:bg-accent",
								isSelected && "bg-muted",
							)}
						>
							<div className="flex w-full items-center">
								<div className="flex items-center gap-2">
									<div className="font-semibold">{name}</div>
									{!item.isRead && (
										<span className="flex size-2 rounded-full bg-primary" />
									)}
									{item.hasAttachments && (
										<PaperclipIcon
											aria-label="Has attachments"
											className="size-3 text-muted-foreground"
										/>
									)}
								</div>
								<div
									className={cn(
										"ml-auto text-xs",
										isSelected ? "text-foreground" : "text-muted-foreground",
									)}
								>
									{formatDistanceToNow(new Date(item.receivedAt), {
										addSuffix: true,
									})}
								</div>
							</div>
							<div className="text-xs font-medium">
								{item.subject || "(no subject)"}
							</div>
							<div className="line-clamp-2 w-full text-xs text-muted-foreground">
								{item.snippet}
							</div>
							{item.labels.length > 0 ? (
								<div className="flex items-center gap-2 pt-1">
									{item.labels.map((label) => (
										<Badge
											key={label}
											variant={getBadgeVariantFromLabel(label)}
										>
											{label}
										</Badge>
									))}
								</div>
							) : null}
						</Button>
					);
				})}
				{canLoadMore && onLoadMore && (
					<div className="flex justify-center p-2">
						<Button
							variant="outline"
							size="sm"
							className="text-muted-foreground"
							onClick={onLoadMore}
						>
							Load more
						</Button>
					</div>
				)}
			</div>
		</ScrollArea>
	);
}

function getBadgeVariantFromLabel(
	label: string,
): ComponentProps<typeof Badge>["variant"] {
	const l = label.toLowerCase();
	if (l === "important" || l === "starred") return "default";
	if (l === "personal") return "outline";
	return "secondary";
}
